import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import useIcons from "../../../hooks/useIcons";
import { getDocumentStatusBadge } from "../../../helpers/getDocumentStatusBadge";
import { fetchDocumentRequest, fetchDocumentSuccess } from "../../../../../redux/actions/autoLombardActions";
import { getMimeType } from "../../../helpers/common.helpers";
import { viewReports } from "../../../../../helpers/data-constants";
import { generateCreditCalculationReport } from "../../../../../redux/actions/autoLombardActions";
import "../requestDetails.scss";
import {AutolombardDocumentTypes} from "../../../constants/constants";
import {iconNames} from "../../../constants/icon-names";

const DocumentViewer = ({ document, fileName }) => {

  const mimeType = useMemo(() => getMimeType(fileName), [fileName]);

  if (!document) return null;

  const src = `data:${mimeType};base64,${document}`;

  const renderContent = () => {
    if (mimeType.startsWith('image/')) {
      return <img src={src} alt={fileName} className="document-viewer-image" />;
    }
    
    if (mimeType === 'application/pdf') {
      return (
        <embed 
          src={src} 
          type="application/pdf" 
          className="document-viewer-embed" 
          title={fileName}
        />
      );
    }
    return (
      <div className="document-viewer">
        <p>Формат файла не поддерживается для просмотра</p>
        <a href={src} download={fileName}>Скачать файл</a>
      </div>
    );
  };

  return (
    <div className="document-viewer">
      {renderContent()}
    </div>
  );
};

const RequestDocumentsInfo = ({ details: { documents = [], credit_report_calculation, client_info } }) => {
  const dispatch = useDispatch();
  const { getIcon } = useIcons();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { loadedDocument } = useSelector(({ autolombard }) => autolombard.document);

  const allDocuments = useMemo(() => {
    if (credit_report_calculation) {
      return [
        ...documents,
        {
          id: client_info.oneAppId,
          fileName: 'Расчет ежемесячных платежей',
          documentType: AutolombardDocumentTypes.MONTHLY_PAYMENT_CALCULATION,
          createdAt: moment().toISOString(),
        }
      ];
    }
    return documents;
  }, [documents, credit_report_calculation]);

  const handleDocumentClick = (document) => {
    if (!document?.id) return;

    if (document.documentType === AutolombardDocumentTypes.MONTHLY_PAYMENT_CALCULATION) {
      const bodyReport = { clientId: document.id };
      dispatch(generateCreditCalculationReport(
        {code: viewReports.CREDIT_REPORT_CALCULATION,
          reportData: bodyReport
        }
      ));
      return;
    }

    dispatch(fetchDocumentRequest({ 
      documentId: document.id, 
      documentType: document.documentType 
    }));
  };

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const getDocumentUrl = (doc) => {
      if (!doc?.document) return "#";
      const mimeType = getMimeType(doc.fileName);
      return `data:${mimeType};base64,${doc.document}`;
  };

  useEffect(() => {
    if (loadedDocument?.document) {
      setIsModalOpen(true);
    }
  }, [loadedDocument]);

  useEffect(() => {
    return () => dispatch(fetchDocumentSuccess(null))
  }, []);

  return (
    <>
      <table className="documents table-container">
        <thead>
          <tr className="table-header">
            <th className="table-header-cell">Имя документа</th>
            <th className="table-header-cell nowrap">Дата создания</th>
            <th className="table-header-cell">Статус</th>
            <th className="table-header-cell">Действия</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {allDocuments.map((doc, index) => (
            <tr key={doc.id || index}>
              <td className="table-cell table-text">{doc.fileName}</td>
              <td className="table-cell">
                {moment(doc.createdAt).format("DD.MM.YYYY")}
              </td>
              <td className="table-cell">
                {getDocumentStatusBadge(doc.status)}
              </td>
              <td className="table-cell">
                <div className="flex-space">
                  <button
                    type="button"
                    className="button-icontrigger caption"
                    onClick={() => handleDocumentClick(doc)}
                  >
                    {getIcon(iconNames.EYE)} Просмотр
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isModalOpen && (
        <div className="wrapper-overlay">
          <div className="content">
            <div className="modal-header">
              <h3>Просмотр документа</h3>
                <div className="d-flex">
                {getMimeType(loadedDocument?.fileName).startsWith('image/') && 
                  <a href={getDocumentUrl(loadedDocument)}
                    download={loadedDocument?.fileName}
                    className="button-icontrigger caption download-link"
                  >
                    <span className="icon-wrapper">{getIcon(iconNames.DOWNLOAD)}</span>
                    <span className="text">Скачать файл</span>
                  </a>}
                <button className="button-icontrigger" onClick={handleClose}>
                  <span className="icon">{getIcon(iconNames.CROSS)}</span>
                </button>
              </div>
            </div>
            <DocumentViewer document={loadedDocument.document} fileName={loadedDocument?.fileName}/>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestDocumentsInfo;
